/* global Office */
import { OutlookException } from 'common/exceptions/outlook/OutlookException';
import { OutlookWrongItemSelectedException } from 'common/exceptions/outlook/OutlookWrongItemSelectedException';
import { AddInFeatureNotSupportedException } from '../exceptions/application/AddInFeatureNotSupportedException';
import { SupportedOfficeItem } from './SupportedOfficeItem';

export function getAsyncBody(body: Office.Body) {
  return new Promise<string>((resolve, reject) => {
    body.getAsync('html', (bodyResult) => {
      if (bodyResult.status === Office.AsyncResultStatus.Succeeded)
        resolve(bodyResult.value);
      else
        reject(
          processError('Failed to get outlook item body', bodyResult.error)
        );
    });
  });
}

export function getAsyncRecipients(recipients: Office.Recipients) {
  const promise = new Promise<Office.EmailAddressDetails[]>(function (
    resolve,
    reject
  ) {
    recipients.getAsync((recipientsResult) => {
      if (recipientsResult.status === Office.AsyncResultStatus.Succeeded)
        resolve(recipientsResult.value);
      else
        reject(
          processError(
            'Failed to get outlook item recipients',
            recipientsResult.error
          )
        );
    });
  });

  return promise;
}

export function getAsyncOrganizer(organizer: Office.Organizer) {
  const promise = new Promise<Office.EmailAddressDetails>(function (
    resolve,
    reject
  ) {
    organizer.getAsync((organizerResult) => {
      if (organizerResult.status === Office.AsyncResultStatus.Succeeded)
        resolve(organizerResult.value);
      else
        reject(
          processError(
            'Failed to get outlook item organizer',
            organizerResult.error
          )
        );
    });
  });
  return promise;
}

export function getAsyncSubject(subject: Office.Subject) {
  const promise = new Promise<string>(function (resolve, reject) {
    subject.getAsync((recipientsResult) => {
      if (recipientsResult.status === Office.AsyncResultStatus.Succeeded)
        resolve(recipientsResult.value);
      else
        reject(
          processError(
            'Failed to get outlook item subject',
            recipientsResult.error
          )
        );
    });
  });
  return promise;
}

export function getAsyncTime(time: Office.Time) {
  const promise = new Promise<Date>(function (resolve, reject) {
    time.getAsync((recipientsResult) => {
      if (recipientsResult.status === Office.AsyncResultStatus.Succeeded)
        resolve(recipientsResult.value);
      else
        reject(
          processError(
            'Failed to get outlook item time',
            recipientsResult.error
          )
        );
    });
  });
  return promise;
}

export function saveAsync(appointment: Office.AppointmentCompose) {
  // if API < 1.3
  if (!appointment.saveAsync) {
    throw new AddInFeatureNotSupportedException(
      'Failed to get outlook item id: saving item is not supported in this version of outlook'
    );
  }

  const promise = new Promise<string>(function (resolve, reject) {
    appointment.saveAsync((saveResult) => {
      if (saveResult.status === Office.AsyncResultStatus.Succeeded) {
        resolve(saveResult.value);
      } else {
        // on macOS "Internal Error" will be returned with code 5001
        if (saveResult.error.code == 5001) {
          reject(
            new AddInFeatureNotSupportedException(
              'Failed to get item id: saving item is not supported in MacOS version of outlook'
            )
          );
        }
        reject(processError('Failed to save outlook item', saveResult.error));
      }
    });
  });
  return promise;
}

export function getAsyncId(appointment: Office.AppointmentCompose) {
  // if API < 1.8
  if (!appointment.getItemIdAsync) {
    throw new AddInFeatureNotSupportedException(
      'Failed to get outlook item id: not supported in this version of outlook'
    );
  }

  const promise = new Promise<string>(function (resolve, reject) {
    appointment.getItemIdAsync((getIdResult) => {
      if (getIdResult.status === Office.AsyncResultStatus.Succeeded) {
        resolve(getIdResult.value);
      } else {
        // on macOS "Internal Error" will be returned with code 5001
        // I assume that getItemIdAsync is not supported on Mac, just like the saveAsync
        // although I didn't find any mention of this
        if (getIdResult.error.code == 5001) {
          reject(
            new AddInFeatureNotSupportedException(
              'Failed to get item id: getting item is not supported in MacOS version of outlook'
            )
          );
        }
        reject(
          processError('Failed to get outlook item id', getIdResult.error)
        );
      }
    });
  });
  return promise;
}

export function getAsyncCustomProperties(
  item: SupportedOfficeItem
): Promise<Office.CustomProperties> {
  return new Promise<Office.CustomProperties>((resolve, reject) => {
    item.loadCustomPropertiesAsync((asyncResult) => {
      if (asyncResult.status == Office.AsyncResultStatus.Succeeded)
        resolve(asyncResult.value);
      else
        reject(
          processError(
            'Failed to get outlook item custom properties',
            asyncResult.error
          )
        );
    });
  });
}

export function saveAsyncCustomProperties(
  properties: Office.CustomProperties
): Promise<void> {
  return new Promise<void>(function (resolve, reject) {
    properties.saveAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) resolve();
      else
        reject(
          processError(
            'Failed to save outlook item custom properties',
            asyncResult.error
          )
        );
    });
  });
}

/**
 * Checks if item is a recurring appointment's master item
 * NOTE: this check is supported only on API >= 1.7. On lower versions always returns false
 * Add-in must check this again later using Graph API. This can't be done here because
 * if item wasn't saved yet, we can't retrieve item's ID for Graph API requests
 */
export function isRecurringMaster(item: SupportedOfficeItem): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {
    // if NOT appointment OR API < 1.7, return false
    if (!item.recurrence) {
      resolve(false);
    } else if (item.recurrence.recurrenceType && !item.seriesId) {
      resolve(true);
    } else if (!item.recurrence.getAsync) {
      resolve(false);
    } else {
      item.recurrence.getAsync((asyncResult) => {
        if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
          if (
            asyncResult.value &&
            asyncResult.value.recurrenceType &&
            !item.seriesId
          ) {
            resolve(true);
          } else {
            resolve(false);
          }
        } else {
          reject(
            processError(
              'Failed to check if outlook item is recurring master',
              asyncResult.error
            )
          );
        }
      });
    }
  });
}

export function getSharedProperties(
  item: SupportedOfficeItem
): Promise<Office.SharedProperties | undefined> {
  return new Promise<Office.SharedProperties | undefined>((resolve, reject) => {
    if (!item.getSharedPropertiesAsync) {
      return resolve(undefined);
    }

    item.getSharedPropertiesAsync((asyncResult) => {
      if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
        resolve(asyncResult.value);
      } else {
        reject(
          processError(
            'Failed to get outlook item shared properties',
            asyncResult.error
          )
        );
      }
    });
  });
}

export function getAttachments(
  item: SupportedOfficeItem
): Promise<Office.AttachmentDetailsCompose[]> {
  return new Promise<Office.AttachmentDetailsCompose[]>((resolve, reject) => {
    if (item.attachments) {
      resolve(item.attachments);
    } else if (item.getAttachmentsAsync) {
      item.getAttachmentsAsync((asyncResult) => {
        if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
          resolve(asyncResult.value);
        } else {
          reject(
            processError(
              'Failed to get outlook item attachments',
              asyncResult.error
            )
          );
        }
      });
    } else {
      reject(
        new AddInFeatureNotSupportedException(
          'Failed to get outlook item attachments: not supported in this version of outlook'
        )
      );
    }
  });
}

export function getAttachmentContent(
  item: SupportedOfficeItem,
  attachmentId: string
): Promise<Office.AttachmentContent> {
  return new Promise<Office.AttachmentContent>((resolve, reject) => {
    if (item.getAttachmentContentAsync) {
      item.getAttachmentContentAsync(attachmentId, (asyncResult) => {
        if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
          resolve(asyncResult.value);
        } else {
          reject(
            processError(
              'Failed to get outlook item attachment content',
              asyncResult.error
            )
          );
        }
      });
    } else {
      reject(
        new AddInFeatureNotSupportedException(
          'Failed to get outlook item attachment content: not supported in this version of outlook'
        )
      );
    }
  });
}

function processError(message: string, error: Office.Error) {
  if (error && error.name === 'GenericResponseError' && error.code === 9020) {
    // see JSDoc for this class
    return new OutlookWrongItemSelectedException(error);
  }
  return new OutlookException(message, error);
}
